import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Box,
  Button,
  Dialog,
  Link,
  Paper,
  Popper,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { useLocation, useNavigate } from "react-router-dom";

// Sidebar entries, matched by the data-tour attribute each layout puts on its menu items.
// An entry the user cannot see (permissions) is skipped.
const PARENT_STEPS = [
  { key: "Dashboard", title: "Dashboard", text: "Your home page: an overview of your children and their progress." },
  { key: "Students", title: "Students", text: "Add your children and manage their accounts and passwords." },
  { key: "Progress", title: "Progress", text: "Reports, readiness and test scores for each child." },
  { key: "Student Results", title: "Student Results", text: "Mock exam, practice paper and worksheet results." },
  { key: "Billing", title: "Billing", text: "Your purchases and invoices." },
  { key: "Notifications", title: "Notifications", text: "Messages and updates about your account." },
  { key: "Setting", title: "Settings", text: "Your details, password and parent PIN." },
];

const STUDENT_STEPS = [
  { key: "Dashboard", title: "Dashboard", text: "Your home page: what to do next and how you are getting on." },
  { key: "Homework", title: "Homework", text: "Your assignments, results and class resources." },
  { key: "Video", title: "Video", text: "Watch your course videos and recordings." },
  { key: "Work Sheet", title: "Work Sheet", text: "Practise with worksheets and check your results." },
  { key: "Mock Exams", title: "Mock Exams", text: "Sit mock exams. Exams you have bought are shown first." },
  { key: "Practice Papers", title: "Practice Papers", text: "Practise papers, including the ones you have bought." },
  { key: "Flash Cards", title: "Flash Cards", text: "Learn and quiz yourself with flash cards." },
  { key: "Classes", title: "Classes", text: "Your live class timetable." },
  { key: "Games", title: "Games", text: "Play learning games and climb the leaderboard." },
  { key: "Certificates", title: "Certificates", text: "Certificates you have earned." },
];

const TUTOR_STEPS = [
  { key: "Dashboard", title: "Dashboard", text: "Your home page: an overview of your classes and students." },
  { key: "Students", title: "Students", text: "See the students in your groups and their progress." },
  { key: "Courses", title: "Courses", text: "The courses you teach." },
  { key: "Groups", title: "Groups", text: "Your class groups and who is in them." },
  { key: "My Content", title: "My Content", text: "Upload and share resources with your groups." },
  { key: "Classes", title: "Classes", text: "Schedule and join your live classes." },
  { key: "Assignments", title: "Assignments", text: "Set homework and review submitted work." },
  { key: "Worksheets", title: "Worksheets", text: "Create and assign worksheets." },
  { key: "Notifications", title: "Notifications", text: "Messages and updates about your account." },
];

const PORTALS = {
  student: {
    steps: STUDENT_STEPS,
    heading: "Welcome to your Student Portal",
    description: "Practise mock exams and practice papers, do your homework and track your progress.",
    // Same lines as My Learning on the public site and the Getting started card (2 Nov 2026).
    who: [
      ["You", "Practise: mock exams, practice papers, worksheets, flash cards and games."],
      ["Your parent", "Buys papers and plans, sees your results, and holds the PIN for the parent view."],
    ],
  },
  tutor: {
    steps: TUTOR_STEPS,
    heading: "Welcome to your Tutor Dashboard",
    description: "Manage your classes, groups and students, share resources and set assignments — all in one place.",
  },
  parent: {
    steps: PARENT_STEPS,
    heading: "Welcome to your Parent Dashboard",
    description:
      "Access your purchased products (Practice Papers, Skills Packs, Masterclasses Videos etc), create child accounts and assign products.",
    who: [
      ["You, the parent", "Buy papers and plans, assign them, see results and weak topics, and manage billing, the PIN and your child's login."],
      ["Your child", "Practises on the app or the student portal, and opens the parent view only with your PIN."],
    ],
  },
};

const HIGHLIGHT = "0 0 0 3px #667eea, 0 0 0 8px rgba(102,126,234,0.35)";

// With the menu in an overlay drawer, look inside that drawer: the same labels may also exist
// on a hidden copy of the menu, and the first match in the DOM would be the wrong one.
const findTarget = (key, inDrawer = false) =>
  document.querySelector(`${inDrawer ? ".MuiDrawer-modal " : ""}[data-tour="${key}"]`);

// Long enough for MUI's drawer slide-in (225ms) to finish before the popper measures its anchor.
const DRAWER_OPEN_DELAY_MS = 300;

const doneKey = (portal, userId) => `${portal}TourDone:${userId ?? "unknown"}`;

/**
 * Welcome prompt on a parent's first visit, and whenever checkout links here with ?welcome=1:
 * "Take the tour" walks the sidebar, "Skip for now" dismisses it. Either way the first-visit
 * prompt is not shown again to that parent.
 *
 * `onRequestDrawer(open)` is for layouts whose menu is hidden behind the hamburger (phones):
 * the tour opens that drawer while it runs and closes it afterwards. Omit it when the menu
 * is already on screen.
 */
export default function ParentWelcomeTour({ userId, firstName, portal = "parent", onRequestDrawer }) {
  const config = PORTALS[portal] || PORTALS.parent;
  const theme = useTheme();
  const isPhone = useMediaQuery(theme.breakpoints.down("sm"));
  const inDrawer = typeof onRequestDrawer === "function";
  const location = useLocation();
  const navigate = useNavigate();
  const [phase, setPhase] = useState("closed"); // closed | welcome | tour
  const [stepIndex, setStepIndex] = useState(0);
  const [steps, setSteps] = useState([]);

  const markDone = useCallback(() => {
    try {
      localStorage.setItem(doneKey(portal, userId), "1");
    } catch {
      /* private mode: the prompt may reappear, which is harmless */
    }
  }, [portal, userId]);

  const checkedRef = useRef(false);

  useEffect(() => {
    // Wait for the parent's id: the "already seen" flag is stored per parent.
    if (userId == null || checkedRef.current) return;
    checkedRef.current = true;

    const params = new URLSearchParams(location.search);
    // The link from checkout (?welcome=1) always opens it; otherwise only the first visit does.
    const forced = params.get("welcome") === "1";
    let done = false;
    try {
      done = localStorage.getItem(doneKey(portal, userId)) === "1";
    } catch {
      done = false;
    }
    if (forced) {
      // The query string has served its purpose; drop it so a refresh does not reopen this.
      params.delete("welcome");
      navigate({ pathname: location.pathname, search: params.toString() }, { replace: true });
    }
    if (forced || !done) setPhase("welcome");
  }, [portal, userId, location.search, location.pathname, navigate]);

  const close = useCallback(() => {
    markDone();
    if (phase === "tour" && inDrawer) onRequestDrawer(false);
    setPhase("closed");
  }, [markDone, phase, inDrawer, onRequestDrawer]);

  const startTour = () => {
    const visible = config.steps.filter((step) => findTarget(step.key, inDrawer));
    if (visible.length === 0) {
      close();
      return;
    }
    setSteps(visible);
    setStepIndex(0);
    if (!inDrawer) {
      setPhase("tour");
      return;
    }
    onRequestDrawer(true);
    setPhase("closed");
    setTimeout(() => setPhase("tour"), DRAWER_OPEN_DELAY_MS);
  };

  const current = phase === "tour" ? steps[stepIndex] : null;
  const anchor = current ? findTarget(current.key, inDrawer) : null;

  useEffect(() => {
    if (!anchor) return undefined;
    const previous = anchor.style.boxShadow;
    anchor.style.boxShadow = HIGHLIGHT;
    anchor.scrollIntoView?.({ block: "nearest" });
    return () => {
      anchor.style.boxShadow = previous;
    };
  }, [anchor]);

  if (phase === "welcome") {
    return (
      <Dialog
        open
        onClose={close}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { background: "#eaf6ff", borderRadius: 3, p: { xs: 2, sm: 4 } } }}
      >
        <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", textAlign: "center", gap: 2 }}>
          <Typography variant="h5" sx={{ fontWeight: 600 }}>
            ✨ {config.heading}{firstName ? `, ${firstName}` : ""}! ✨
          </Typography>
          <Typography>{config.description}</Typography>
          {config.who ? (
            <Box sx={{ width: "100%", display: "grid", gridTemplateColumns: { xs: "1fr", sm: "repeat(2, minmax(0, 1fr))" }, gap: 1.5, textAlign: "left" }}>
              {config.who.map(([title, text]) => (
                <Box key={title} sx={{ bgcolor: "#fff", borderRadius: 2, p: 1.75, border: "1px solid #d6e9f8" }}>
                  <Typography sx={{ fontSize: 12, fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase", color: "#2B3485" }}>
                    {title}
                  </Typography>
                  <Typography sx={{ fontSize: 14, mt: 0.5, color: "#374151" }}>{text}</Typography>
                </Box>
              ))}
            </Box>
          ) : null}
          <Box sx={{ mt: 2, display: "flex", flexDirection: "column", alignItems: "center", gap: 2 }}>
            <Button variant="contained" size="large" onClick={startTour} sx={{ textTransform: "none", fontWeight: 700, px: 4 }}>
              Take the tour
            </Button>
            <Link component="button" type="button" underline="always" onClick={close} sx={{ fontWeight: 600 }}>
              Skip for now
            </Link>
          </Box>
        </Box>
      </Dialog>
    );
  }

  if (phase === "tour" && current && anchor) {
    const last = stepIndex === steps.length - 1;
    return (
      // On phones the open drawer leaves no room to its right, so the card goes under the item.
      <Popper open anchorEl={anchor} placement={isPhone ? "bottom-start" : "right-start"} sx={{ zIndex: 2000 }}>
        <Paper
          elevation={8}
          sx={{
            p: 2,
            ml: isPhone ? 0 : 1.5,
            mt: isPhone ? 1 : 0,
            maxWidth: "min(280px, calc(100vw - 32px))",
          }}
        >
          <Typography sx={{ fontWeight: 700 }}>{current.title}</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            {current.text}
          </Typography>
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1 }}>
            <Typography variant="caption" color="text.secondary">
              {stepIndex + 1} / {steps.length}
            </Typography>
            <Box sx={{ display: "flex", gap: 1 }}>
              <Button size="small" onClick={close} sx={{ textTransform: "none" }}>
                Skip
              </Button>
              {stepIndex > 0 && (
                <Button size="small" onClick={() => setStepIndex((i) => i - 1)} sx={{ textTransform: "none" }}>
                  Back
                </Button>
              )}
              <Button
                size="small"
                variant="contained"
                onClick={() => (last ? close() : setStepIndex((i) => i + 1))}
                sx={{ textTransform: "none" }}
              >
                {last ? "Done" : "Next"}
              </Button>
            </Box>
          </Box>
        </Paper>
      </Popper>
    );
  }

  return null;
}
